import {
  STATUS_CHIP_BY_STEP,
  NEXT_ACTION_BY_STEP,
  DELETION_STEP_KEYS,
  getStepsForBranch,
  getStepIndex,
} from "./foiaSteps";
import { EMAIL_TEMPLATES, formatAppointmentLine } from "./foiaEmailTemplates";

const DAY_MS = 24 * 60 * 60 * 1000;

const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

// Date-only values are calendar days, so build them in local time instead of letting
// new Date("YYYY-MM-DD") read them as UTC midnight and show the previous day.
export function parseCalendarDate(value) {
  const match = String(value || "").match(ISO_DATE_PREFIX);
  if (!match) return new Date(value);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function formatDate(isoString) {
  if (!isoString) return "—";
  const date = parseCalendarDate(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function formatDateTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatRelativeTime(isoString) {
  if (!isoString) return "—";
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / (60 * 1000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(isoString);
}

export function daysUntil(isoString) {
  const due = parseCalendarDate(isoString).setHours(0, 0, 0, 0);
  const today = new Date().setHours(0, 0, 0, 0);
  return Math.round((due - today) / DAY_MS);
}

export const TERMINAL_STEP_KEYS = new Set(["closed", "closed_not_retained"]);

/** "overdue" | "soon" | "normal" | null (null once retention is done — closed requests and the deletion half have no due-date flag). */
export function getDueDateUrgency(request) {
  if (DELETION_STEP_KEYS.has(request.currentStepKey)) return null;
  if (TERMINAL_STEP_KEYS.has(request.currentStepKey)) return null;
  const days = daysUntil(request.dueDate);
  if (days <= 2) return "overdue";
  if (days <= 7) return "soon";
  return "normal";
}

export function getStatusChip(request) {
  return STATUS_CHIP_BY_STEP[request.currentStepKey] || { label: "In progress", variant: "default" };
}

export function getNextActionText(request) {
  return NEXT_ACTION_BY_STEP[request.currentStepKey] || "No next action.";
}

export function getCurrentStepDefinition(request) {
  const steps = getStepsForBranch(request.branch);
  const index = getStepIndex(request.branch, request.currentStepKey);
  return steps[index] || null;
}

/** Plain-English label for the list/engineering screens — who or what the request is waiting on. */
export function getCurrentStepPlainEnglish(request) {
  const step = getCurrentStepDefinition(request);
  if (!step) return "In progress";
  if (TERMINAL_STEP_KEYS.has(request.currentStepKey)) return step.title;
  if (step.waitingOnLabel) return `Waiting on ${step.waitingOnLabel}`;
  return step.title;
}

export function getAssignee(request) {
  const step = getCurrentStepDefinition(request);
  return step?.waitingOnLabel || "—";
}

export function isClosed(request) {
  return TERMINAL_STEP_KEYS.has(request.currentStepKey);
}

export function buildEmailDraft(request, stepKey) {
  const template = EMAIL_TEMPLATES[stepKey];
  if (!template) return { subject: "", body: "" };

  const appointmentLines = request.appointments
    .map((appt) => formatAppointmentLine(appt, formatDate))
    .join("\n");

  return {
    subject: template.subject(request),
    body: template.body(request, { appointmentLines }),
  };
}
